'use client';
import { Button } from '@curology/ui-components-web/react/Button';
import { Typography } from '@curology/ui-components-web/react/Typography';

import { useNavigation } from '@/components/flow/hooks/useNavigation';
import { useRouter } from '@/hooks/useRouter';
import FLOW_TYPES, {
  type FlowType,
} from '@/services/funnelService/consts/flowType';
import { type LiveConsultation } from '@/services/liveConsultationService/types';
import {
  formatDateTimestamp,
  formatScheduledTimeRange,
} from '@/services/liveConsultationService/utils';
import { useFlag } from '@/utils/featureFlags/client';
import { urls } from '@/utils/urls';

const ConsultationCard = ({
  consultation,
  timezone,
  flowType,
}: {
  consultation: LiveConsultation;
  timezone: string;
  flowType: FlowType;
}) => {
  const navigation = useNavigation();
  const router = useRouter();
  const showPreferredLanguage = useFlag('show-preferred-language');
  const isInResubscriptionFlow =
    flowType === FLOW_TYPES.Resubscription ||
    flowType === FLOW_TYPES.ResubscriptionNonExpedited;

  const onNext = () => {
    if (isInResubscriptionFlow) {
      // TODO: Make this work for agency if needed
      router.push(
        `${urls.curology.legacy.patientDashboard.manageSubscription.customize}`,
      );
    } else {
      navigation.onNext();
    }
  };

  return (
    <div className="cur-bg-surface-neutral cur-border-2 cur-border-subtle cur-border-l-white cur-border-r-white cur-rounded-sm cur-text-primary cur-px-10 cur-py-12 md:cur-border-l-subtle md:cur-border-r-subtle">
      <Typography size="h1" className="cur-text-center">
        Your call is scheduled 👍
      </Typography>
      <dl className="cur-flex cur-flex-col cur-mt-8 cur-space-y-6">
        <div className="cur-flex cur-items-center cur-justify-between">
          <Typography as="dt" size="h3">
            Date
          </Typography>
          <Typography
            as="dd"
            size="body"
            weight="bold"
            className="cur-text-subtle cur-mb-0"
          >
            {formatDateTimestamp(
              consultation.scheduledAtDateTimestamp,
              timezone,
            )}
          </Typography>
        </div>
        <div className="cur-flex cur-items-center cur-justify-between">
          <Typography as="dt" size="h3">
            Time
          </Typography>
          <Typography
            as="dd"
            size="body"
            weight="bold"
            className="cur-text-subtle cur-mb-0"
          >
            {formatScheduledTimeRange(
              consultation.scheduledAtTimestamp,
              timezone,
            )}
          </Typography>
        </div>
        {showPreferredLanguage && (
          <div className="cur-flex cur-items-center cur-justify-between">
            <Typography as="dt" size="h3">
              Preferred language
            </Typography>
            <Typography
              as="dd"
              size="body"
              weight="bold"
              className="cur-text-subtle cur-mb-0"
            >
              {consultation.preferredLanguage}
            </Typography>
          </div>
        )}
        <hr />
      </dl>
      <div className="cur-mt-12 cur-text-center cur-space-y-6">
        <Typography
          as="p"
          size="body"
          weight="bold"
          className="!cur-text-subtle"
        >
          We’ll call you at the time you picked. <br /> We can’t wait to meet
          you!
        </Typography>
        <Typography
          as="p"
          size="body"
          weight="bold"
          className="!cur-text-subtle"
        >
          Look out for your custom Treatment Plan <br /> shortly after the call.
        </Typography>
        <Button onClick={onNext} className="cur-min-w-[200px]">
          Continue
        </Button>
        <Typography size="meta">
          Message and data rates may apply. You can reschedule anytime from the
          link in your confirmation email.
        </Typography>
      </div>
    </div>
  );
};

export default ConsultationCard;
